import React, { useState, useRef, ChangeEvent } from 'react';
import './CallFlowEditor.css';

interface SubStep {
	instruction: string;
	dialogScripts: string[];
}

interface Step {
	stepName: string;
	steps: SubStep[];
}

const DEFAULT_JSON: Step[] = [
	{
		stepName: 'Address Search',
		steps: [
			{
				instruction: 'Greeting',
				dialogScripts: ['Thanks for calling Brightspeed, my name is [agent name].', 'How can I help you today?'],
			},
		],
	},
];

export default function CallFlowEditor() {
	const [flowData, setFlowData] = useState<Step[]>(DEFAULT_JSON);
	const [currentIndex, setCurrentIndex] = useState(0);
	const [jsonText, setJsonText] = useState(JSON.stringify(DEFAULT_JSON, null, 2));
	const fileInputRef = useRef<HTMLInputElement>(null);

	const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;
		const reader = new FileReader();
		reader.onload = (evt) => {
			try {
				const json = JSON.parse(evt.target?.result as string);
				setFlowData(json);
				setJsonText(JSON.stringify(json, null, 2));
				setCurrentIndex(0);
				alert('JSON file loaded!');
			} catch {
				alert('Invalid JSON file.');
			}
		};
		reader.readAsText(file);
	};

	const handleDownload = () => {
		const blob = new Blob([JSON.stringify(flowData, null, 2)], { type: 'application/json' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = 'edited_flow_data.json';
		a.click();
		URL.revokeObjectURL(url);
	};

	const handleLoadJson = () => {
		try {
			const json = JSON.parse(jsonText);
			setFlowData(json);
			setCurrentIndex(0);
			alert('JSON loaded from text!');
		} catch {
			alert('Invalid JSON format in textarea.');
		}
	};

	const addStep = (type: 'before' | 'after' | 'end') => {
		const newStep: Step = { stepName: 'New Step', steps: [] };
		const updated = [...flowData];
		if (type === 'before') updated.splice(currentIndex, 0, newStep);
		else if (type === 'after') updated.splice(currentIndex + 1, 0, newStep);
		else updated.push(newStep);
		setFlowData(updated);
		setCurrentIndex(type === 'after' ? currentIndex + 1 : currentIndex);
	};

	const deleteSubStep = (idx: number) => {
		const updated = [...flowData];
		updated[currentIndex].steps.splice(idx, 1);
		setFlowData(updated);
	};

	const addSubStep = () => {
		const updated = [...flowData];
		updated[currentIndex].steps.push({ instruction: '', dialogScripts: [''] });
		setFlowData(updated);
	};

	const insertSubStepAbove = (idx: number) => {
		const updated = [...flowData];
		updated[currentIndex].steps.splice(idx, 0, {
			instruction: 'New sub-instruction (inserted above).',
			dialogScripts: ['New sub-dialog script (inserted above).'],
		});
		setFlowData(updated);
	};

	const updateInstruction = (idx: number, val: string) => {
		const updated = [...flowData];
		updated[currentIndex].steps[idx].instruction = val;
		setFlowData(updated);
	};

	const updateDialogScripts = (idx: number, val: string) => {
		const updated = [...flowData];
		updated[currentIndex].steps[idx].dialogScripts = val.split('\n');
		setFlowData(updated);
	};

	const deleteMainStep = () => {
		if (flowData.length <= 1) {
			alert('Cannot delete the last main step.');
			return;
		}
		const updated = [...flowData];
		updated.splice(currentIndex, 1);
		setFlowData(updated);
		setCurrentIndex(Math.max(currentIndex - 1, 0));
	};

	return (
		<div className="page-container">
			<h1 className="callflow-heading">Call Flow Script Editor</h1>

			<div className="file-controls">
				<label>
					Upload Call Flow JSON File:
					<input type="file" ref={fileInputRef} onChange={handleFileUpload} accept=".json,application/json" />
				</label>
			</div>
			<h1 className="callflow-heading">Load from Text (Optional)</h1>
			<div className="controls">
				<textarea
					className="json-text"
					rows={5}
					value={jsonText}
					onChange={(e) => setJsonText(e.target.value)}
					placeholder="Paste your JSON here..."
				/>
				<button className="load-json-btn" onClick={handleLoadJson}>
					Load from Text Area
				</button>
			</div>

			<h1 className="callflow-heading">Step Management</h1>
			<label className="sub-step">Main Steps</label>

			<div className="editor-controls-header">
				<div className="button-group">
					<button className="add-btn" onClick={() => addStep('before')}>
						Add Before Current
					</button>
					<button className="add-btn" onClick={() => addStep('after')}>
						Add After Current
					</button>
					<button className="add-btn" onClick={() => addStep('end')}>
						Add to End
					</button>
				</div>
				<button className="preview-json-btn" onClick={() => setJsonText(JSON.stringify(flowData, null, 2))}>
					Preview Generated JSON
				</button>
			</div>

			<div id="navigation-controls">
				<button className="navigation-btn" onClick={() => setCurrentIndex(Math.max(currentIndex - 1, 0))}>
					&laquo; Previous Step
				</button>
				<div className="nav-center-group">
					<label>Go to:</label>
					<select value={currentIndex} onChange={(e) => setCurrentIndex(Number(e.target.value))}>
						{flowData.map((step, i) => (
							<option value={i} key={i}>
								{step.stepName || `Step ${i + 1}`}
							</option>
						))}
					</select>
					<span>
						Step {currentIndex + 1} of {flowData.length}
					</span>
				</div>
				<button
					className="navigation-btn"
					onClick={() => setCurrentIndex(Math.min(currentIndex + 1, flowData.length - 1))}
				>
					Next Step &raquo;
				</button>
			</div>

			<div className="main-container">
				<div className="editor-panel">
					<h1 className="callflow-heading">Edit Current Step Details</h1>
					<div>
						<label>Main Step Name:</label>
						<input
							type="text"
							value={flowData[currentIndex].stepName}
							onChange={(e) => {
								const updated = [...flowData];
								updated[currentIndex].stepName = e.target.value;
								setFlowData(updated);
							}}
						/>
					</div>

					<h3>Sub Steps</h3>
					{flowData[currentIndex].steps.map((subStep, idx) => (
						<div key={idx} className="substep-card">
							<label>Instruction:</label>
							<input type="text" value={subStep.instruction} onChange={(e) => updateInstruction(idx, e.target.value)} />
							<label>Dialog Scripts (one per line):</label>
							<textarea
								className="json-text"
								rows={3}
								value={subStep.dialogScripts.join('\n')}
								onChange={(e) => updateDialogScripts(idx, e.target.value)}
							/>
							<button className="delete-btn" onClick={() => deleteSubStep(idx)}>
								Delete Sub Step
							</button>
							<button className="insert-btn" onClick={() => insertSubStepAbove(idx)}>
								Insert Above
							</button>
						</div>
					))}
					<button className="add-substep-btn" onClick={addSubStep}>
						Add Sub Step to End
					</button>
					<button className="delete-main-btn" onClick={deleteMainStep}>
						Delete Main Step
					</button>
				</div>

				<div className="preview-panel">
					<h1 className="callflow-heading">Live Preview</h1>
					{flowData[currentIndex].steps.map((subStep, i) => (
						<div key={i} className="preview-block">
							<div className="instruction-label">Instruction (Sub-Step {i + 1}):</div>
							<div className="instruction-box">{subStep.instruction || <i>No Instruction</i>}</div>
							<div className="instruction-label">Dialogs (Sub-Step {i + 1}):</div>
							<div className="dialog-list">
								{subStep.dialogScripts.length > 0 ? (
									subStep.dialogScripts.map((line, idx) => (
										<div key={idx} className="dialog-bubble">
											{line || <i>(empty)</i>}
										</div>
									))
								) : (
									<i>No Dialog Scripts</i>
								)}
							</div>
						</div>
					))}
				</div>
			</div>

			<h1 className="callflow-heading">Generated JSON (Raw Output)</h1>
			<pre className="json-output">{JSON.stringify(flowData, null, 2)}</pre>
			<button className="download-btn" onClick={handleDownload}>
				Download Edited JSON
			</button>
		</div>
	);
}
